import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button, ButtonGroup, Grid } from "@mui/material";
// @ts-ignore
import {
    FIELD_TYPES,
    StriimAsterisk,
    StriimTooltip,
    StriimTypography,
    useStriimForm,
    StriimMessageBox
} from "@striim/striim-ui";

import App from "app";
import indexedDB from "./tables-indexedDB";
import Click from "/app/images/striimline/click.svg";
import Text from "/app/images/striimline/Text.svg";
import Sql from "/app/images/striimline/sql.svg";
import ContentVisualSelection from "./ContentVisualSelection";
import ContentTxt from "./contentTxt";
import ContentSql from "./contentSql";
import SelectTablesModal from "./select-tables-modal/select-tables-modal";
import { styles } from "./selectTables.styles";
import { currentSelectionInit, useSelectTables } from "./select-tables-context";
import TestConnectionModal from "./components/test-connection-modal";
import utils from "core/utils";
import { isAppAdapter } from "src/modules/wizards/utils/checkAdapterType";
import {
    TableWrapper,
    WhatIsTable
} from "app/components/appwizard/component-wizards/wizards/source/database-reader/configs/db-providers.utils";
import { DB_PROVIDERS } from "app/components/appwizard/component-wizards/wizards/source/database-reader/configs/db-providers";
import { AppVentFDAdapterTestConnectionStatus } from "src/generic/fd-form-builder/fd-form-builder.utils";
import { StriimModal } from "@striim/striim-ui-v2";
import {
    getCurrentDbType,
    buildDataFromTablesValue,
    parseMultiQueryToQueryItems,
    parseSingleQueryToQueryItems
} from "./selectTables-utils";
import { TestConnectionStatus } from "src/generic/test-connection/test-connection";
import { QueryItem } from "./components/add-queries-modal/add-queries-modal";
import FieldResetModal from "./components/field-reset-modal/field-reset-modal";

export enum Tabs {
    visual = "Visual",
    manual = "Manual",
    query = "query"
}

export const CONNECTION_FAILURE_ERROR = "Unable to connect to the source. Check connection properties.";

const TABS = [Tabs.visual, Tabs.manual, Tabs.query];

interface SelectTablesProps {
    name: string;
    metaObjectId: string;
    handler: string;
    isCDC: boolean;
    dbType: string;
    schemaType: TableWrapper;
    entityType: WhatIsTable;
    connectionFields?: any[];
    adapterName: string;
    isCacheComponent?: boolean;
    formFields: any[];
}

const SelectTables: React.FC<SelectTablesProps> = ({
    name,
    metaObjectId,
    handler,
    isCDC,
    dbType,
    schemaType,
    entityType,
    connectionFields,
    adapterName,
    isCacheComponent = false,
    formFields,
    ...props
}) => {
    const { currentSelection, setCurrentSelection, setSourceInfo } = useSelectTables();
    const { values: formValues, setFieldValue, errors, submitCount } = useStriimForm();

    const [selectedContent, setSelectedContent] = useState<Tabs>();
    const selectedContentRef = useRef<Tabs>();
    selectedContentRef.current = selectedContent;
    const previousTabRef = useRef<Tabs>();
    const [showSelectTablesModal, setShowSelectTablesModal] = useState<boolean>(false);
    const [showTestConnectionModal, setShowTestConnectionModal] = useState<boolean>(false);
    const [isEditFlow, setIsEditFlow] = useState<boolean>(false);
    const [areConnectionFieldsValid, setAreConnectionFieldsValid] = useState<boolean>(!connectionFields?.length);
    const [error, setError] = useState<{ timestamp: number; message: string } | null>(null);
    const [testConnectionStatus, setTestConnectionStatus] = useState<TestConnectionStatus>(() =>
        // Not used in ContentVisualSelection for case when switching between Visual/Manual/SQL tabs
        !!currentSelection.schemas
            ? TestConnectionStatus.Success
            : error?.message || tablesError
            ? TestConnectionStatus.Fail
            : TestConnectionStatus.Default
    );
    const [hasSelectTablesFailed, setHasSelectTablesFailed] = useState<boolean>(false);

    const tablesRef = useRef<{
        tab: string;
        schemas?: number;
        entities?: number;
        timestamp?: number;
        data?: any[];
        queries?: QueryItem[];
        hasError?: boolean;
    } | null>(null);

    const isVisualWithTables = () => (tablesRef.current?.schemas ?? 0) > 0 && selectedContentRef.current === TABS[0];
    const isQueryWithQueries = () =>
        selectedContentRef.current === TABS[2] && (tablesRef.current?.queries?.length ?? 0) > 0;

    const isVisualTableSelectionFilled = isQueryWithQueries() || isVisualWithTables();

    const resetSelection = useCallback(() => {
        tablesRef.current = currentSelectionInit;
        setCurrentSelection(currentSelectionInit);
    }, [setCurrentSelection]);

    useEffect(() => {
        const isSourceAnAppAdapter = isAppAdapter(adapterName);
        setSourceInfo({
            schemaType,
            entityType,
            dbType,
            isAppAdapter: isSourceAnAppAdapter,
            isCDC
        });

        (async () => {
            let tableSelectedData = {
                tab: Tabs.visual
            };
            try {
                tableSelectedData = await indexedDB.getDataById(metaObjectId);
            } catch (e) {
                console.error("Error fetching tables config from IndexedDB:", e);
            }

            const formKeys = formValues ? Object.keys(formValues) : [];
            const queryValue = formKeys.find(k => k.toLowerCase() === "query");
            const multiQueryValue = formKeys.find(k => k.toLowerCase() === "multiquery");
            if (formValues?.[queryValue]?.trim() || formValues?.[multiQueryValue]?.trim()) {
                const parsedQueries = formValues?.[multiQueryValue]?.trim()
                    ? parseMultiQueryToQueryItems(formValues[multiQueryValue])
                    : parseSingleQueryToQueryItems(formValues?.[queryValue]);
                tablesRef.current = {
                    tab: Tabs.query,
                    queries: parsedQueries
                };
                setCurrentSelection({
                    ...tableSelectedData,
                    data: [],
                    queries: parsedQueries
                });
                setSelectedContent(Tabs.query);
                previousTabRef.current = Tabs.query;
            } else if (tableSelectedData?.tab === Tabs.visual && !!props?.value?.length) {
                const { containerName, ...data } = buildDataFromTablesValue(props.value);
                tablesRef.current = {
                    ...tableSelectedData,
                    queries: [],
                    ...data
                };
                setSelectedContent(tableSelectedData.tab);
                setCurrentSelection({
                    ...tableSelectedData,
                    ...data,
                    ...(containerName && { containerName })
                });
                previousTabRef.current = tableSelectedData.tab;
            } else if (props?.value) {
                tablesRef.current = {
                    tab: Tabs.manual
                };
                setCurrentSelection({ schemas: null, data: [] });
                setSelectedContent(Tabs.manual);
                previousTabRef.current = Tabs.manual;
            } else {
                tablesRef.current = {
                    tab: Tabs.visual
                };
                previousTabRef.current = Tabs.visual;
                setSelectedContent(Tabs.visual);
            }
        })();

        App.vent.on("source:saved", async function(metaObjectId) {
            if (metaObjectId) {
                await indexedDB.putData({
                    id: metaObjectId,
                    tab: tablesRef?.current?.tab,
                    timestamp: tablesRef?.current?.timestamp,
                    hasError: tablesRef?.current?.hasError
                });
            }
        });

        App.vent.on("cache:saved", async function(metaObjectId) {
            if (metaObjectId) {
                await indexedDB.putData({
                    id: metaObjectId,
                    tab: tablesRef?.current?.tab,
                    timestamp: tablesRef?.current?.timestamp,
                    hasError: tablesRef?.current?.hasError
                });
            }
        });

        App.vent.on(AppVentFDAdapterTestConnectionStatus, (status: TestConnectionStatus) => {
            setTestConnectionStatus(status);
            setHasSelectTablesFailed(false);
            if (status === TestConnectionStatus.Success) {
                setError(null);
            }
        });

        return () => {
            App.vent.off("source:saved");
            App.vent.off("cache:saved");
            App.vent.off(AppVentFDAdapterTestConnectionStatus);
        };
    }, [entityType]);

    useEffect(() => {
        if (formValues && !!connectionFields?.length) {
            const useConnectionProfile = !!formValues["useConnectionProfile"];
            const areConnectionFieldsValid = useConnectionProfile
                ? !!formValues["connectionProfileName"]
                : connectionFields
                      .filter(field => field.name !== "useConnectionProfile" && field.name !== "connectionProfileName")
                      .every(field => (formValues?.hasOwnProperty(field.name) ? !!formValues[field.name] : true));
            setAreConnectionFieldsValid(areConnectionFieldsValid);
        }
    }, [formValues, connectionFields]);

    //Finds the name of the entity property from the property template (eg: Tables, sObjects)
    const entityPropertyName = useMemo(() => {
        if (formFields?.length) {
            return formFields.find(item => item.type === FIELD_TYPES.SELECTTABLES).name;
        } else {
            return "Tables";
        }
    }, [formFields]);

    const handleTabGroupChange = (index: number) => {
        setSelectedContent(TABS[index]);
    };
    const checkContent = (index: number) => selectedContent === TABS[index];

    const isVisualSelection = checkContent(0);
    const isTxt = checkContent(1);
    const isSql = checkContent(2);

    const keys = formValues ? Object.keys(formValues) : [];
    const excludeTablesKey = keys.find(k => k.toLowerCase() === "excludedtables");
    const queryKey = keys.find(k => k.toLowerCase() === "query");
    const multiQueryKey = keys.find(k => k.toLowerCase() === "multiquery");
    const hasExcludeTables = !!excludeTablesKey;
    const hasQuery = !!queryKey;
    const hasMultiQuery = !!multiQueryKey;

    const handleIncludeTablesChange = (value: string, isReset = false) => {
        tablesRef.current = {
            tab: Tabs.manual
        };
        if (!isReset) {
            previousTabRef.current = Tabs.manual;
        }
        props.onChange(value);
        hasMultiQuery && setFieldValue(multiQueryKey, "");
    };
    const handleExcludeTables = value => {
        setFieldValue(excludeTablesKey, value);
    };

    const handleSqlChange = value => {
        props.onChange("");
        hasExcludeTables && setFieldValue(excludeTablesKey, null);
        //For upgrade scenarios - if the query ever had a value, isLegacyQuery would be set to true, to inform the backend of the legacy query value
        if (formValues?.[queryKey]?.trim() && !formValues?.[multiQueryKey]?.trim()) {
            setFieldValue("isLegacyQuery", true);
        }
        hasMultiQuery && setFieldValue(multiQueryKey, value);
        hasQuery && setFieldValue(queryKey, "");
    };

    const handleCheck = () => {
        setShowTestConnectionModal(true);
        setError(null);
        setIsEditFlow(!!currentSelection.data?.length);
    };

    const handleSetTables = useCallback(
        (tables: string) => {
            setHasSelectTablesFailed(false);
            setFieldValue(entityPropertyName, tables);
        },
        [entityPropertyName, setFieldValue]
    );

    const handleSelectTablesError = useCallback((message: string) => {
        setError({ timestamp: Date.now(), message });
    }, []);

    const handleCancelOnSelectTables = (hasConnectionFailed, message) => {
        hasConnectionFailed
            ? setError({ timestamp: Date.now(), message })
            : setHasSelectTablesFailed(!currentSelection.schemas);
    };

    const resetListAndQuerySelections = () => {
        previousTabRef.current = Tabs.visual;
        hasQuery && setFieldValue(queryKey, "");
        hasMultiQuery && setFieldValue(multiQueryKey, "");
        hasExcludeTables && setFieldValue(excludeTablesKey, null);
        handleIncludeTablesChange("", true);
        setCurrentSelection({ schemas: null, entities: 0, data: [], hasError: false });
    };

    const resetVisualAndQuerySelections = () => {
        previousTabRef.current = Tabs.manual;
        hasQuery && setFieldValue(queryKey, "");
        hasMultiQuery && setFieldValue(multiQueryKey, "");
        setCurrentSelection({ schemas: null, entities: 0, data: [], hasError: false });
    };

    const resetVisualAndListSelections = () => {
        setCurrentSelection({ schemas: null, entities: 0, data: [] });
        props.onChange("");
        hasExcludeTables && setFieldValue(excludeTablesKey, null);
    };

    const handleCancelModeSwitch = useCallback(() => {
        setSelectedContent(previousTabRef.current);
    }, []);

    const handleTestConnectionVerifySuccess = useCallback(() => {
        setShowTestConnectionModal(false);
        setShowSelectTablesModal(true);
    }, []);

    const handleTestConnectionCancel = useCallback(
        (hasConnectionFailed: boolean, message: string) => {
            setShowTestConnectionModal(false);
            handleCancelOnSelectTables(hasConnectionFailed, message);
        },
        [handleCancelOnSelectTables]
    );

    const enableAction = props.enabled || false;
    const tablesError = submitCount
        ? errors?.[entityPropertyName] || !props.formRef?.current?.values?.[entityPropertyName]
            ? "Value for Include is required"
            : null
        : testConnectionStatus === TestConnectionStatus.Success
        ? hasSelectTablesFailed
        : "";

    const databaseProviderType = props.formRef?.current?.values?.DatabaseProviderType?.value;
    const currentDbType = getCurrentDbType(dbType, databaseProviderType);

    return (
        <>
            <FieldResetModal
                isVisualTableSelectionFilled={isVisualTableSelectionFilled}
                selectedContent={selectedContent}
                entityType={entityType}
                resetSelection={resetSelection}
                formikformRef={props.formRef}
                entityPropertyName={entityPropertyName}
                queryKey={queryKey}
                multiQueryKey={multiQueryKey}
            />
            <Grid container data-testid="select-tables">
                <Grid container mb={2} justifyContent={"space-between"}>
                    <Grid item>
                        <StriimTypography variant="body4" sx={styles.sectionTitle} data-testid="section-title">
                            {isSql
                                ? "Queries"
                                : utils.capitalize(
                                      dbType === DB_PROVIDERS.SALESFORCE.id
                                          ? "Object"
                                          : dbType === DB_PROVIDERS.ZENDESK.id
                                          ? "Zendesk Object"
                                          : entityType
                                  ) + "s"}{" "}
                            <StriimAsterisk />
                        </StriimTypography>
                    </Grid>
                    <Grid item sx={[styles.titleContent, styles.buttonGroup]} data-testid="button-group">
                        <ButtonGroup onChange={handleTabGroupChange} disabled={!enableAction}>
                            <StriimTooltip title={`Visual Mode: Select ${entityType}s visually`} placement="top">
                                <Button
                                    data-testid="visual-selection-tab"
                                    onClick={() => handleTabGroupChange(0)}
                                    sx={
                                        !enableAction
                                            ? styles.disabledButton
                                            : isVisualSelection
                                            ? styles.activeButton
                                            : styles.tabButton
                                    }
                                >
                                    <Click />
                                </Button>
                            </StriimTooltip>
                            <StriimTooltip title={`List Mode: Enter a list of ${entityType}s`} placement="top">
                                <Button
                                    data-testid="manual-selection-tab"
                                    onClick={() => handleTabGroupChange(1)}
                                    sx={
                                        !enableAction
                                            ? styles.disabledButton
                                            : isTxt
                                            ? styles.activeButton
                                            : styles.tabButton
                                    }
                                >
                                    <Text />
                                </Button>
                            </StriimTooltip>
                            {hasQuery && (
                                <StriimTooltip title={`Query Mode: Enter a list of queries`} placement="top">
                                    <Button
                                        data-testid="query-selection-tab"
                                        onClick={() => handleTabGroupChange(2)}
                                        sx={
                                            !enableAction
                                                ? styles.disabledButton
                                                : isSql
                                                ? styles.activeButton
                                                : styles.tabButton
                                        }
                                    >
                                        <Sql />
                                    </Button>
                                </StriimTooltip>
                            )}
                        </ButtonGroup>
                    </Grid>
                </Grid>

                <Grid container sx={styles.content}>
                    {isVisualSelection ? (
                        <>
                            <ContentVisualSelection
                                enableAction={enableAction}
                                onClickAction={handleCheck}
                                success={!!currentSelection.schemas}
                                onIncludeTablesChange={handleIncludeTablesChange}
                                onEdit={handleCheck}
                                entityType={entityType?.toLowerCase() || "table"}
                                schemaType={schemaType?.toLowerCase() || "schema"}
                                adapterName={adapterName}
                                showWarning={
                                    (props?.value?.length && !currentSelection.data?.length) ||
                                    !!formValues?.[multiQueryKey] ||
                                    (!formValues?.[multiQueryKey]?.length && !!formValues?.[queryKey])
                                }
                                tablesError={tablesError}
                                error={error}
                                areConnectionFieldsValid={areConnectionFieldsValid}
                                testConnectionStatus={testConnectionStatus}
                                previousTab={previousTabRef.current}
                                onSwitchToVisualMode={resetListAndQuerySelections}
                                onCancellingModeSwitch={handleCancelModeSwitch}
                            />
                            {showTestConnectionModal ? (
                                <TestConnectionModal
                                    dbType={currentDbType}
                                    isCDC={isCDC}
                                    formValues={props.formRef?.current.values ?? {}}
                                    metaObjectId={metaObjectId}
                                    adapterName={adapterName}
                                    onVerifySuccess={handleTestConnectionVerifySuccess}
                                    onCancel={handleTestConnectionCancel}
                                />
                            ) : showSelectTablesModal ? (
                                <SelectTablesModal
                                    showSelectTablesModal
                                    setShowSelectTablesModal={setShowSelectTablesModal}
                                    isEditFlow={isEditFlow}
                                    formValues={props.formRef?.current?.values}
                                    dbType={currentDbType}
                                    schemaType={schemaType}
                                    entityType={entityType}
                                    isCDC={isCDC}
                                    metaObjectId={metaObjectId}
                                    setTables={handleSetTables}
                                    tablesRef={tablesRef}
                                    adapterName={adapterName}
                                    onError={handleSelectTablesError}
                                    onCancel={handleCancelOnSelectTables}
                                />
                            ) : null}
                        </>
                    ) : null}
                    {isTxt && (
                        <ContentTxt
                            name={name}
                            enableAction={enableAction}
                            onIncludeTablesChange={handleIncludeTablesChange}
                            includeTables={props?.value || ""}
                            handleExcludeTables={handleExcludeTables}
                            excludeTables={formValues?.ExcludedTables}
                            hasExcludeTables={hasExcludeTables}
                            entityType={entityType?.toLowerCase() || "table"}
                            tablesError={tablesError}
                            previousTab={previousTabRef.current}
                            onSwitchToListMode={resetVisualAndQuerySelections}
                            onCancellingModeSwitch={handleCancelModeSwitch}
                            adapterName={adapterName}
                        />
                    )}
                    {isSql && (
                        <ContentSql
                            setSqlCode={handleSqlChange}
                            enableAction={enableAction}
                            testConnectionStatus={testConnectionStatus}
                            adapterName={adapterName}
                            databaseProviderType={databaseProviderType}
                            isCacheComponent={isCacheComponent}
                            previousTab={previousTabRef.current}
                            tablesRef={tablesRef}
                            hasPreviousSelections={
                                !!currentSelection.schemas || !!props?.value || !!formValues?.ExcludedTables
                            }
                            metaObjectId={metaObjectId}
                            onCancellingModeSwitch={handleCancelModeSwitch}
                            onSwitchToQueryMode={resetVisualAndListSelections}
                        />
                    )}
                </Grid>
            </Grid>
        </>
    );
};

export default SelectTables;
